/*
 * Data shaping for the app detail modal.
 *
 * The payload's detail fields are all slightly off from what the design asks
 * for, and each mismatch is load-bearing, so the reshaping lives here rather
 * than inline in the modal:
 *
 *  - `versionInfo` is one changelog string, not a list of bullets.
 *  - `previousVersionInfo` is an object keyed "Version 1.0", each value a
 *    [note, downloadLink] pair — a 2-tuple with no date, so release-history rows
 *    have no date to render.
 *  - `testingLink` / `appleLink` / `googleLink` are '' or null when unset, never
 *    absent, so presence has to be tested by truthiness rather than by key.
 */

/** Changelog bullets for "What's New". One real line usually means one bullet. */
export function whatsNewLines(versionInfo) {
  if (typeof versionInfo !== 'string') return [];
  return versionInfo
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

/**
 * Release-history rows, in the order the payload lists them — the payload's own
 * ordering is the only ordering available, since the entries carry no dates.
 */
export function releaseHistory(previousVersionInfo) {
  if (!previousVersionInfo || typeof previousVersionInfo !== 'object') return [];
  return Object.entries(previousVersionInfo).map(([key, value]) => {
    const pair = Array.isArray(value) ? value : [];
    return {
      version: key.replace(/^Version\s+/, ''),
      note: pair[0] || '',
      downloadLink: pair[1] || '',
    };
  });
}

/**
 * One page of `rows`, plus how many pages there are and which one this is.
 *
 * `page` is clamped rather than trusted: the caller holds it in state, so it
 * outlives any particular list, and an empty list still has one (empty) page so
 * a pager never has to render "of 0".
 */
export function paginate(rows, page, size) {
  const list = Array.isArray(rows) ? rows : [];
  const pageCount = Math.max(1, Math.ceil(list.length / size));
  const current = Math.min(Math.max(page, 1), pageCount);
  const start = (current - 1) * size;
  return { rows: list.slice(start, start + size), page: current, pageCount };
}

/**
 * What the primary download button should do.
 *
 * Preserves the behavior the old per-app pages had: a visitor on the platform an
 * app is published for goes to that store, everyone else gets the APK.
 */
export function downloadTarget(app, os) {
  if (os === 'iOS' && app.appleLink) {
    return { label: 'View on the App Store', href: app.appleLink, external: true };
  }
  if (os === 'Android' && app.googleLink) {
    return { label: 'View on Google Play', href: app.googleLink, external: true };
  }
  return { label: 'Download APK', href: app.downloadLink, external: false };
}

/** The optional test build, or null when the app has none. */
export function testBuild(app) {
  if (!app.testingLink) return null;
  return { href: app.testingLink, version: app.testingVersion };
}

/**
 * The `?app=` query that deep-links one app's modal. Built through
 * `URLSearchParams` so the one navLink containing a literal space survives the
 * round trip.
 */
export function appSearch(navLink) {
  return `?${new URLSearchParams({ app: navLink }).toString()}`;
}
